/**
 * Maps server (Cloud Functions) errors to translated message keys so riders
 * never see raw English server text.
 *
 * The server stays backward compatible with every installed app, so we match
 * on the stable `reason` code when the server sends one, and otherwise on the
 * server's message text. Unknown errors return null — callers then show their
 * existing generic translated message, never the raw server string.
 */

const reasonOf = (err) => String(err?.details?.reason || err?.customData?.details?.reason || '');
const codeOf = (err) => String(err?.code || '');
const msgOf = (err) => String(err?.message || '').toLowerCase();

/** Errors from createRide / getRideQuote → `book.*` key, or null. */
export const bookingErrorKey = (err) => {
  const reason = reasonOf(err);
  const msg = msgOf(err);
  if (reason === 'tip_changed') return 'book.errTipChanged';
  if (reason === 'waiting_terms_changed') return 'book.errWaitingTermsChanged';
  if (reason === 'note_has_phone') return 'riderNote.noPhone';
  if (reason === 'zone_inactive' || msg.includes('not available in this area')) return 'book.errZoneInactive';
  if (msg.includes('too close')) return 'book.errTooClose';
  if (msg.includes('trip too long')) return 'book.errTooLong';
  if (codeOf(err).includes('resource-exhausted') || msg.includes('too many requests')) return 'book.errTooMany';
  if (msg.includes('account blocked') || msg.includes('account is unavailable')) return 'book.errBlocked';
  if (msg.includes('intertown request')) return 'book.finishIntertownFirst';
  if (msg.includes('rider name')) return 'book.riderNameRequired';
  if (msg.includes('rider phone') || msg.includes('customer phone')) return 'book.riderPhoneRequired';
  return null;
};

/** Errors from boostRideFare → { key, vars } using `active.*`, or null. */
export const boostErrorKey = (err) => {
  const msg = msgOf(err);
  if (msg.includes('maximum boost tip')) {
    const cap = String(err?.message || '').match(/₹\s*(\d+)/);
    return { key: 'active.boostMax', vars: { cap: cap ? cap[1] : '' } };
  }
  if (msg.includes('cannot boost') || msg.includes('not searching')) return { key: 'active.boostTooLate' };
  if (codeOf(err).includes('resource-exhausted') || msg.includes('too many requests')) return { key: 'active.boostWait' };
  return null;
};

/** Errors from the Intertown callables → `intertown.*` key, or null. */
export const intertownErrorKey = (err) => {
  const reason = reasonOf(err);
  const msg = msgOf(err);
  if (reason === 'destination_too_close' || msg.includes('too close')) return 'intertown.errTooClose';
  if (reason === 'pickup_outside_area') return 'intertown.errPickupOutside';
  if (reason === 'destination_outside_route' || reason === 'destination_out_of_range') return 'intertown.errDestinationRange';
  if (msg.includes('already have an active intertown')) return 'intertown.errAlreadyActive';
  if (msg.includes('bookings are paused') || msg.includes('not available yet') || msg.includes('currently unavailable')) {
    return 'intertown.errUnavailable';
  }
  if (msg.includes('account is unavailable') || msg.includes('account blocked')) return 'book.errBlocked';
  if (codeOf(err).includes('resource-exhausted') || msg.includes('too many requests')) return 'book.errTooMany';
  return null;
};

/**
 * Decide what a failed createRide means. Returns one of:
 *   'paused' | 'active' | 'outside' | 'known' (use `key`) | 'generic'
 *
 * A bare `failed-precondition` used to be read as "you already have an active
 * ride", which mislabelled "town not live yet" and "outside service area".
 * The server's real active-ride error always carries `details.rideId`.
 */
export const classifyBookingError = (err) => {
  const code = codeOf(err);
  const reason = reasonOf(err);
  const rawMsg = String(err?.message || '');
  const msg = rawMsg.toLowerCase();
  const rideId = err?.details?.rideId || err?.customData?.details?.rideId || null;
  const key = bookingErrorKey(err);
  const outside = code.includes('outside') || rawMsg.includes('service area');
  const paused = reason === 'booking_paused' || reason === 'emergency_stop';

  if (paused) return { kind: 'paused', key: null, rideId };
  if (rideId || msg.includes('active ride')
    || (code.includes('failed-precondition') && !outside && !key)) {
    return { kind: 'active', key: null, rideId };
  }
  if (outside) return { kind: 'outside', key: null, rideId };
  if (key) return { kind: 'known', key, rideId };
  return { kind: 'generic', key: null, rideId };
};
